import { useCallback, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Button from "@components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useCurrentMedia, usePredictionsForFile } from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { uploadSourceForReview } from "@rapid/utils/api/datasets";
import type { ApproveSourceAnnotation } from "@rapid/utils/api/datasets";

export function NeedsEditingButton({ isHovered }: { isHovered: boolean }) {
    const navigate = useNavigate();
    const { workspaceUrl, workflowUrl } = useParams<{
        workspaceUrl: string;
        workflowUrl: string;
    }>();
    const currentMedia = useCurrentMedia();
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const { currentWorkspace: workspace } = useCurrentWorkspace();
    const { flowLocked } = useFlow();
    const [isLoading, setIsLoading] = useState(false);

    // file = parent media item (video or image). Predictions are keyed by file ID.
    // frame = what the user is actually seeing (extracted PNG for videos, same as file for images).
    const fileId = currentMedia?.file?.id;
    const frameSegment = currentMedia?.frameSegment ?? 0;
    const predictionsForFile = usePredictionsForFile(fileId);

    // Get predictions for the current frame segment (0 for images, time-based for video frames)
    const currentPredictions = useMemo(() => {
        if (!predictionsForFile) return [];
        return predictionsForFile[frameSegment] || [];
    }, [predictionsForFile, frameSegment]);

    const rapidDataId = workflow?.rapidDataId;
    // Show button once inference has run on the current frame segment,
    // even if predictions are empty (model found nothing — still worth editing).
    const hasInferenceRun = predictionsForFile ? frameSegment in predictionsForFile : false;

    const handleClick = useCallback(async () => {
        if (!fileId || !rapidDataId || isLoading) return;

        // Use the frame (not the parent file) to resolve the image source.
        // For images: frame === file, so this is a no-op.
        // For videos: frame is the extracted PNG for the current frame segment.
        // We read uploadResult at click time because it's set via mutation after upload
        // completes and won't trigger a Zustand re-render.
        const frameItem = currentMedia?.frame || currentMedia?.file;
        const isExistingSource = !!frameItem?.file?.imageUrl;
        const existingSourceId = isExistingSource ? frameItem?.id : undefined;
        const uploadImageUrl = !isExistingSource ? frameItem?.uploadResult?.downloadUrl : undefined;

        if (!isExistingSource && !uploadImageUrl) {
            console.error("[NeedsEditingButton] No image source available");
            return;
        }

        setIsLoading(true);

        try {
            // Build annotation payload from current predictions
            const annotation: ApproveSourceAnnotation = {
                image: {
                    width:
                        frameItem?.metadata?.serverMediaDimensions?.width ||
                        frameItem?.metadata?.naturalDimensions?.width ||
                        640,
                    height:
                        frameItem?.metadata?.serverMediaDimensions?.height ||
                        frameItem?.metadata?.naturalDimensions?.height ||
                        480
                },
                predictions: currentPredictions.map((pred: any) => ({
                    x: pred.x,
                    y: pred.y,
                    width: pred.width,
                    height: pred.height,
                    class: pred.class || pred.label || "",
                    confidence: pred.confidence
                }))
            };

            // Send to rapid-review batch.
            // For existing sources: just adds to batch (fast, no re-upload).
            // For user uploads: downloads from Firebase Storage URL and uploads to dataset.
            const { sourceId } = await uploadSourceForReview({
                rapidDataId,
                ...(existingSourceId
                    ? { sourceId: existingSourceId }
                    : { imageUrl: uploadImageUrl! }),
                annotation
            });

            // Navigate to improve flow with the sourceId
            navigate(`/${workspaceUrl}/${workflowUrl}/improve/${sourceId}`);
        } catch (error) {
            console.error("[NeedsEditingButton] Error uploading source for review:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        fileId,
        rapidDataId,
        isLoading,
        currentMedia,
        currentPredictions,
        navigate,
        workspaceUrl,
        workflowUrl
    ]);

    if (!hasInferenceRun || !rapidDataId || !workspace) {
        return null;
    }

    return (
        <div
            className={twClassNames(
                "absolute right-4 top-4 z-10 transition-opacity duration-200",
                isHovered && !flowLocked
                    ? "pointer-events-auto opacity-100"
                    : "pointer-events-none opacity-0"
            )}
        >
            <Button
                type="secondary"
                size="small"
                onClick={handleClick}
                disabled={isLoading}
                className="shadow-lg"
            >
                <i className={isLoading ? "far fa-spinner fa-spin" : "far fa-pen-to-square"} />
                <span>Needs editing?</span>
            </Button>
        </div>
    );
}
